import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, CircularProgress, Typography } from '@mui/material';
import { RootState } from '@stores/store';
import { setToastMessage } from '@stores/App/slice';
import { TOAST_TYPES } from '@constants/constants';
import { useCopyAppAccessClipboard } from '../../hooks/useCopyAppAccessClipboard';
import { SubmoduleFilterRegistry } from '../../hooks/useSubmoduleFilterRegistry';
import {
  AppAccess,
  AppAccessType,
  DashboardSelectorModuleItem,
  DashboardSelectorSubmoduleItem,
} from '../../types/dashboardSelector';
import { buildListableDashboardModules, getDemoGameFilterId } from '../../utils/utils';
import { splitListableModulesBySelection } from '../../utils/splitCatalogBySelection';
import DashboardSelectorRow from './DashboardSelectorRow';
import AppAccessPasteSnackbar from '../AppAccessPasteSnackbar';
import { Search } from '@components';
import {
  tableWrapperSx,
  sectionHeaderCellSx,
  sectionHeaderLabelSx,
  tableHeaderCellSx,
  tableScrollContainerSx,
  tableSx,
} from './styles';

type DashboardSelectorProps = {
  selectedSubmodules: DashboardSelectorSubmoduleItem[];
  onSelectedSubmodulesChange: Dispatch<SetStateAction<DashboardSelectorSubmoduleItem[]>>;
  isLoading?: boolean;
  isViewOnly?: boolean;
  showAppAccess?: boolean;
  filterRegistry?: SubmoduleFilterRegistry;
  allowedSubmoduleTypes?: string[];
  includePermissionSubmodules?: boolean;
  onRefreshSubmoduleFilters?: (submoduleId: string) => Promise<void>;
  onCustomAccessRequest?: (submodule: DashboardSelectorSubmoduleItem) => void;
};

const CatalogSectionHeader = ({
  label,
  totalCount,
  showAppAccess,
  onClearClipboard,
}: {
  label: string;
  totalCount: number;
  showAppAccess: boolean;
  onClearClipboard?: () => void;
}) => (
  <Box component="tr">
    <Box
      component="td"
      colSpan={showAppAccess ? 3 : 1}
      sx={{
        ...sectionHeaderCellSx,
        borderTop: 'none',
        borderLeft: 'none',
        borderRight: 'none',
      }}
      onMouseDown={onClearClipboard}
    >
      <Typography sx={{ ...sectionHeaderLabelSx, color: '#6C6C6C' }}>
        {label} ({totalCount})
      </Typography>
    </Box>
  </Box>
);

const DashboardSelector = ({
  selectedSubmodules,
  onSelectedSubmodulesChange,
  isLoading = false,
  isViewOnly = false,
  showAppAccess = true,
  filterRegistry,
  allowedSubmoduleTypes,
  includePermissionSubmodules = false,
  onRefreshSubmoduleFilters,
  onCustomAccessRequest,
}: DashboardSelectorProps) => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const modules = useSelector((state: RootState) => state.ModulesData.userManagementModules);

  const handlePasteError = useCallback(
    (message: string) => {
      dispatch(setToastMessage({ type: TOAST_TYPES.ERROR, message }));
    },
    [dispatch],
  );

  const {
    isPasteEnabled,
    pastedSubmoduleIds,
    clipboardNotification,
    clipboardDataSubmoduleId,
    copyAppAccess,
    pasteAppAccess,
    undoPasteAppAccess,
    clearClipboard,
    dismissClipboardNotification,
  } = useCopyAppAccessClipboard({
    setSelectedSubmodules: onSelectedSubmodulesChange,
    ...(showAppAccess && {
      filterRegistry,
      onPasteError: filterRegistry ? handlePasteError : undefined,
    }),
  });

  const listableModules = useMemo(
    () =>
      buildListableDashboardModules(modules, includePermissionSubmodules, allowedSubmoduleTypes),
    [modules, allowedSubmoduleTypes, includePermissionSubmodules],
  );

  const { selectedModulesList, unselectedModulesList, selectedCount, unselectedCount } = useMemo(
    () => splitListableModulesBySelection(listableModules, selectedSubmodules, search),
    [listableModules, selectedSubmodules, search],
  );

  const updateSubmoduleAppAccess = useCallback(
    (submodule: DashboardSelectorSubmoduleItem, appAccess: AppAccess) => {
      onSelectedSubmodulesChange((previous) => {
        const existingIndex = previous.findIndex(
          (item) => item.submoduleId === submodule.submoduleId,
        );

        if (existingIndex === -1) {
          return [...previous, { ...submodule, appAccess }];
        }

        return previous.map((item) =>
          item.submoduleId === submodule.submoduleId ? { ...item, appAccess } : item,
        );
      });
    },
    [onSelectedSubmodulesChange],
  );

  const handleAppAccessChange = (
    submodule: DashboardSelectorSubmoduleItem,
    type: AppAccess['type'],
  ) => {
    switch (type) {
      case AppAccessType.DEMO: {
        const selectedSubmoduleFilters = filterRegistry?.get(submodule.submoduleId);
        if (!selectedSubmoduleFilters) return;
        const demoGameFilterId = getDemoGameFilterId(selectedSubmoduleFilters);
        if (!demoGameFilterId) return;
        updateSubmoduleAppAccess(submodule, { type, filters: [demoGameFilterId] });
        break;
      }
      case AppAccessType.FULL_ACCESS:
        updateSubmoduleAppAccess(submodule, { type, filters: [] });
        break;
      case AppAccessType.CUSTOM:
        onCustomAccessRequest?.(submodule);
        break;
      default:
        break;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const renderDashboardSelectorRow = (
    modules: DashboardSelectorModuleItem[],
    isSelectedSection: boolean,
  ) => {
    return modules.map((module) => (
      <DashboardSelectorRow
        key={module.id}
        module={module}
        selectedSubmodules={selectedSubmodules}
        isSelectedSection={isSelectedSection}
        filterRegistry={filterRegistry}
        isViewOnly={isViewOnly}
        showAppAccess={showAppAccess}
        isPasteEnabled={isPasteEnabled}
        clipboardDataSubmoduleId={clipboardDataSubmoduleId}
        pastedSubmoduleIds={pastedSubmoduleIds}
        onToggleSelect={onSelectedSubmodulesChange}
        onAppAccessChange={handleAppAccessChange}
        onCopyAppAccess={copyAppAccess}
        onPasteAppAccess={pasteAppAccess}
        onClearClipboard={clearClipboard}
      />
    ));
  };
  return (
    <>
      <Box onMouseDown={clearClipboard} sx={tableScrollContainerSx}>
        <Box sx={tableWrapperSx}>
          <Box component="table" sx={tableSx}>
            <Box component="thead">
              <Box component="tr">
                <Box component="th" sx={{ ...tableHeaderCellSx, p: '6px 11px' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      width: '100%',
                      minWidth: 0,
                    }}
                  >
                    <Typography sx={{ ...sectionHeaderLabelSx, flexShrink: 0 }}>Modules</Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Search
                        styles={{
                          width: '100%',
                          height: '32px',
                          border: '1px solid #2F736E1F',
                          backgroundColor: 'transparent',
                        }}
                        handleSearch={() => {}}
                        search={search}
                        setSearch={setSearch}
                      />
                    </Box>
                  </Box>
                </Box>
                {showAppAccess && (
                  <>
                    <Box component="th" sx={tableHeaderCellSx}>
                      <Typography sx={sectionHeaderLabelSx}>App Access</Typography>
                    </Box>
                    <Box component="th" sx={tableHeaderCellSx}>
                      <Typography sx={{ ...sectionHeaderLabelSx, whiteSpace: 'nowrap' }}>
                        Copy/ Paste App Access
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
            <Box component="tbody">
              {selectedModulesList.length > 0 && (
                <>
                  <CatalogSectionHeader
                    label="Selected"
                    totalCount={selectedCount}
                    showAppAccess={showAppAccess}
                    onClearClipboard={clearClipboard}
                  />
                  {renderDashboardSelectorRow(selectedModulesList, true)}
                </>
              )}
              {unselectedModulesList.length > 0 && (
                <>
                  <CatalogSectionHeader
                    label="Unselected"
                    totalCount={unselectedCount}
                    showAppAccess={showAppAccess}
                    onClearClipboard={clearClipboard}
                  />
                  {renderDashboardSelectorRow(unselectedModulesList, false)}
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <AppAccessPasteSnackbar
        notification={showAppAccess ? clipboardNotification : null}
        onClose={dismissClipboardNotification}
        onUndo={undoPasteAppAccess}
      />
    </>
  );
};

export default DashboardSelector;
